export type PasoRitual = 'energia' | 'anoche' | 'pilar' | 'mision' | 'dificil';

/** El paso «Anoche escribiste…» solo existe si anoche se escribió algo en «Mañana». */
export function pasosDelRitual(hayNotaDeAnoche: boolean): PasoRitual[] {
  const todos: PasoRitual[] = ['energia', 'anoche', 'pilar', 'mision', 'dificil'];
  return hayNotaDeAnoche ? todos : todos.filter((p) => p !== 'anoche');
}

/** Para sellar basta la misión: un ritual que exige cinco respuestas no se hace. */
export function puedeSellar(mision: string): boolean {
  return mision.trim().length > 0;
}

const HASTA = 13 * 60;

/**
 * La tarjeta se destaca desde que empieza el día del usuario hasta las 13:00. A las
 * 2:00 con el día empezando a las 4:00 todavía es ayer, y ayer ya pasó de las 13:00.
 */
export function ritualDestacado(minutosLocales: number, inicioDia: number): boolean {
  return minutosLocales >= inicioDia && minutosLocales < HASTA;
}

export function tituloDeTarea(texto: string): string | null {
  const limpio = texto.trim().slice(0, 140);
  return limpio === '' ? null : limpio;
}

/**
 * Lo que el paso de energía debe escribir al avanzar o cerrar: el borrador, solo si
 * están las dos mitades y no es lo que ya hay guardado. Media pregunta no es un
 * registro, y re-guardar lo mismo solo movería la hora.
 */
export function animoAGuardar(
  borrador: { mood: number | null; energy: number | null } | null,
  registrado: { mood: number; energy: number } | null | undefined,
): { mood: number; energy: number } | null {
  if (borrador === null || borrador.mood === null || borrador.energy === null) return null;
  if (registrado && registrado.mood === borrador.mood && registrado.energy === borrador.energy) {
    return null;
  }
  return { mood: borrador.mood, energy: borrador.energy };
}

type Borrador = { mood: number | null; energy: number | null };

/**
 * El ánimo que el ritual enseña y guarda: cada mitad tocada manda, y la que no se
 * ha tocado sale de lo guardado. Así, tocar una mitad antes de que IndexedDB
 * responda no deja la otra en blanco.
 */
export function animoEfectivo(
  borrador: Borrador | null,
  registrado: { mood: number; energy: number } | null | undefined,
): Borrador | null {
  if (borrador === null) return null;
  return {
    mood: borrador.mood ?? registrado?.mood ?? null,
    energy: borrador.energy ?? registrado?.energy ?? null,
  };
}

/** Qué piezas del día ya están acuñadas: el riel «Tu día» del ritual. */
export function piezasDelDia(
  pasos: readonly PasoRitual[],
  estado: { animo: Borrador | null; mision: string; pilar: boolean; dificil: boolean },
): { paso: PasoRitual; llena: boolean }[] {
  const llena: Record<PasoRitual, boolean> = {
    energia: estado.animo !== null && estado.animo.mood !== null && estado.animo.energy !== null,
    // Si el paso existe es porque anoche se escribió algo: llega lleno.
    anoche: true,
    pilar: estado.pilar,
    mision: estado.mision.trim() !== '',
    dificil: estado.dificil,
  };
  return pasos.map((paso) => ({ paso, llena: llena[paso] }));
}

/**
 * Las tareas del paso «Lo difícil»: las pendientes y, aunque ya esté hecha, la que
 * es lo difícil de hoy. Si la hecha desapareciera de la lista, el riel la daría por
 * elegida y el paso no la enseñaría: dos verdades distintas en la misma pantalla.
 */
export function opcionesDeDificil<T extends { id: string; doneAt: string | null }>(
  tareas: readonly T[] | undefined,
  frogTaskId: string | null | undefined,
): T[] {
  return (tareas ?? []).filter((t) => t.doneAt === null || t.id === frogTaskId);
}

/**
 * ¿Se puede hacer de esta tarea lo difícil primero del día? La misma regla en Hoy,
 * en Semana y en el ritual: solo una pendiente, y nunca si la difícil actual ya
 * está hecha. Marcar una hecha convertiría +2 en +20 a toro pasado; cambiar una
 * difícil hecha le quitaría en silencio sus 20 puntos al día.
 */
export function puedeSerDificil(
  tarea: { id: string; doneAt: string | null },
  dificil: { id: string; doneAt: string | null } | null | undefined,
): boolean {
  if (tarea.doneAt !== null) return false;
  if (!dificil) return true;
  return dificil.id !== tarea.id && dificil.doneAt === null;
}

/**
 * Lo que el ritual tiene escrito y sin guardar: lo mismo que guarda al avanzar,
 * cerrar o salir por donde sea (atrás del sistema, cambiar de pantalla, ocultar la
 * página). `null` en lo que no hay que escribir, para no repetir escrituras.
 */
export function pendienteDelRitual(estado: {
  mision: string | null;
  misionGuardada: string | undefined;
  animo: Borrador | null;
  mood: { mood: number; energy: number } | null | undefined;
}): { mision: string | null; animo: { mood: number; energy: number } | null } {
  const mision = estado.mision?.trim() ?? null;
  return {
    mision: mision !== null && mision !== (estado.misionGuardada ?? '').trim() ? mision : null,
    animo: animoAGuardar(animoEfectivo(estado.animo, estado.mood), estado.mood),
  };
}
